import {
  type ElementProps,
  type OpenChangeReason,
  autoUpdate,
  flip,
  hide,
  offset,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
  useTransitionStyles,
} from '@floating-ui/react';
import { disableFocusInside, getNextTabbable } from '@floating-ui/react/utils';
import { interactionResponse } from '@zillow/yield-callback';
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type SyntheticEvent,
  useCallback,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useReducedMotion } from '../../../hooks/use-reduced-motion';
import type { MenuRootPropsInterface } from '../types/menu-root.types';
import type { MenuFloatingContextInterface, UseMenuReturnInterface } from '../types/use-menu.types';

export function useMenu(props: Partial<MenuRootPropsInterface>): {
  menu: UseMenuReturnInterface;
  floating: MenuFloatingContextInterface;
} {
  const {
    defaultOpen,
    offset: offsetValue = 8,
    open: controlledOpen,
    onOpenChange: setControlledOpen,
    overflowPadding = 8,
    placement = 'bottom-start',
    shouldCloseOnViewportLeave = false,
    shouldAwaitInteractionResponse = true,
    useClickProps,
    useDismissProps,
  } = props;

  const elementsRef = useRef<Array<HTMLElement | null>>([]);

  const openedByPointerRef = useRef<boolean>(false);

  const contentId = useId();

  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  /**
   * Accessibility props and handlers to connect group heading with content if not wrapped in group
   */
  const [labelId, setLabelId] = useState<string | undefined>();

  /**
   * Open state management (controlled or uncontrolled)
   */
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = 'open' in props;
  const open = isControlled ? controlledOpen : uncontrolledOpen || false;

  /**
   * Floating UI setup for modal state management
   */
  const { context, refs, middlewareData, floatingStyles } = useFloating({
    placement,

    open,

    onOpenChange: async (state, event, reason) => {
      openedByPointerRef.current = state && reason === 'click';

      if (shouldAwaitInteractionResponse) {
        await interactionResponse();
      }

      updateOpenState(state, event, reason);
    },

    whileElementsMounted: autoUpdate,

    /**
     * Middleware plugins have to be in certain order.
     * See: https://floating-ui.com/docs/useFloating#middleware
     */
    middleware: [
      // Translates the floating element along the specified axes
      offset(offsetValue),

      // Changes the placement of the floating element to keep it in view
      flip({
        fallbackPlacements: ['top-start', 'top-end', 'bottom-start', 'bottom-end'],
        padding: overflowPadding,
      }),

      // A data provider that allows you to hide the floating element
      ...(shouldCloseOnViewportLeave ? [hide({ padding: overflowPadding })] : []),
    ],
  });

  /**
   * Floating UI interaction hooks for accessibility and behavior
   * Sets up click and dismiss event handlers, and ARIA attributes based on role
   */
  const listNav = useListNavigation(context, {
    listRef: elementsRef,
    activeIndex,
    onNavigate: setActiveIndex,
    loop: true,
    disabledIndices: [],
    focusItemOnOpen: false,
  });

  const click = useClick(context, {
    enabled: true,
    event: 'mousedown',
    ...useClickProps,
  });

  const dismiss = useDismiss(context, {
    enabled: true,
    outsidePress: (event) => !(event.target as HTMLElement).closest('[data-c11n-component="Menu"]'),
    ...useDismissProps,
  });

  /**
   * useRole must be kept even though role="menu" is intentionally not placed on the positioner
   * element. It also contributes aria-haspopup="menu", aria-expanded, and aria-controls to the
   * trigger via getReferenceProps, which are required for screen readers to announce the trigger
   * correctly. The role prop is stripped from getFloatingProps in menu-content.tsx.
   */
  const role = useRole(context, {
    role: 'menu',
  });

  /**
   * Handles controlled or uncontrolled "open" state.
   *
   * @param newValue - New state
   * @param event - Event object
   * @param reason - Reason for state change
   */
  const updateOpenState = useCallback(
    (newValue: boolean, event?: Event, reason?: OpenChangeReason) => {
      if (isControlled) {
        setControlledOpen?.(newValue, event, reason);
      } else {
        setUncontrolledOpen(newValue);
        setControlledOpen?.(newValue, event, reason);
      }
    },
    [isControlled, setControlledOpen],
  );

  /**
   * Adds `Tab` key support to move focus between the trigger and menu items, closing the menu
   * when the user tabs past the last item. Arrow key and typeahead navigation are still provided
   * by `useListNavigation` and `useTypeahead`; this interaction only augments the Tab behavior.
   */
  const tabNavigation = useMemo<ElementProps>(
    () => ({
      reference: {
        onKeyDown(event: ReactKeyboardEvent<HTMLElement>) {
          if (!open || event.key !== 'Tab' || event.shiftKey) return;
          event.preventDefault();
          setActiveIndex(0);
          elementsRef.current[0]?.focus();
        },
      },
      item: {
        onKeyDown(event: ReactKeyboardEvent<HTMLElement>) {
          if (event.key !== 'Tab' || event.shiftKey) return;
          const items = elementsRef.current;
          const currentIndex = items.indexOf(event.currentTarget);
          if (currentIndex !== items.length - 1) return;
          event.preventDefault();

          // Make the floating content untabbable before asking floating-ui for the next tabbable
          // element, so the trigger's siblings (not the menu items) are considered. The content
          // is about to unmount so restoring tab indexes is unnecessary.
          const trigger = refs.domReference.current as HTMLElement | null;
          const floating = refs.floating.current;
          if (floating) disableFocusInside(floating);
          trigger?.focus();
          const next = getNextTabbable(trigger);
          if (next && next !== trigger) next.focus();

          updateOpenState(false, event.nativeEvent, 'escape-key');
        },
      },
    }),
    [open, refs, updateOpenState],
  );

  const { getFloatingProps, getReferenceProps, getItemProps } = useInteractions([
    listNav,
    click,
    dismiss,
    role,
    tabNavigation,
  ]);

  /**
   * Motion helpers and styles
   */
  const reducedMotion = useReducedMotion();

  const { isMounted, styles: floatingTransitionStyles } = useTransitionStyles(context, {
    duration: reducedMotion ? 0 : 250,
    initial: {
      opacity: 0,
      transform: 'scale(0.975)',
    },
  });

  /**
   * Handles programmatic close action
   *
   * @param event - Event object
   */
  const onClose = useCallback(
    (event: SyntheticEvent) => {
      updateOpenState(false, event as unknown as Event, 'click');
    },
    [updateOpenState],
  );

  const menu = useMemo(
    () => ({
      // Internal focus management
      activeIndex,
      contentId,
      elementsRef,
      openedByPointerRef,

      // Open/mounted state and close handler
      open,
      isMounted,
      onClose,

      // Trigger and reference refs
      refs,

      // Trigger and reference props
      getReferenceProps,
      getItemProps,

      // Styles
      floatingStyles,
      floatingTransitionStyles,

      // Accessibility props and handlers
      labelId,
      setLabelId,

      middlewareData,
    }),
    [
      activeIndex,
      contentId,
      elementsRef,
      open,
      isMounted,
      onClose,
      openedByPointerRef,
      refs,
      getReferenceProps,
      getItemProps,
      floatingStyles,
      floatingTransitionStyles,
      labelId,
      setLabelId,
      middlewareData,
    ],
  );

  /*
   * Floating-ui-bridged data. Keep it on a separate context so only
   * Menu.Content pays for fresh Floating UI values.
   */
  const floating = { context, getFloatingProps };

  return { menu, floating };
}
